import type { EasyAIProps, ReplaceCallback } from "../src/easy-ai/assistant/types";
import type { ProjectPlannerWaitingPanelProps } from "../src/easy-ai/projectPlannerWaitingPanel/types";
import type { PromptTemplateFormProps } from "../src/easy-ai/promptTemplateForm/types/promptTemplateForm";
import type { EasyAITeaserProps } from "../src/easy-ai/teaser/types";

import type { ProjectStatusReportSidebarProps } from "../src/easy-ai/projectStatusReport/./sidebar/types/projectStatusReportSidebar";
import { ProjectStatusReportFormProps } from "../src/easy-ai/projectStatusReport/form/types";
import { type PromptTemplateStatus } from "../src/shared/types/schemaEnums";

window.EasyAI ||= {};

window.EasyAI.init = async (props: EasyAIProps, appWrapper: string | HTMLElement) => {
  const { init } = await import("../src/easy-ai/assistant/main");
  window.EasyAI.init = init;
  return init(props, appWrapper);
};

window.EasyAI.initFromActivator = async (activator: HTMLElement, props?: EasyAIProps) => {
  const { initFromActivator } = await import("../src/easy-ai/assistant/main");
  window.EasyAI.initFromActivator = initFromActivator;
  initFromActivator(activator, props);
};

window.EasyAI.initWriteAssistant = async (selectedText: string, replaceCallback: ReplaceCallback) => {
  const { initWriteAssistant } = await import("../src/easy-ai/assistant/main");
  window.EasyAI.initWriteAssistant = initWriteAssistant;
  initWriteAssistant(selectedText, replaceCallback);
};

window.EasyAI.initDataSourceQuery = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceQuery } = await import("../src/easy-ai/dataSourceQuery/main");
  window.EasyAI.initDataSourceQuery = initDataSourceQuery;
  initDataSourceQuery(appWrapperId, collectionId);
};

window.EasyAI.initDataSourceWebUrlForm = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceWebUrlForm } = await import("../src/easy-ai/dataSourceWeb/webUrl/main");
  window.EasyAI.initDataSourceWebUrlForm = initDataSourceWebUrlForm;
  initDataSourceWebUrlForm(appWrapperId, collectionId);
};

window.EasyAI.initDataSourceWebCrawlerForm = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceWebCrawlerForm } = await import("../src/easy-ai/dataSourceWeb/webCrawler/main");
  window.EasyAI.initDataSourceWebCrawlerForm = initDataSourceWebCrawlerForm;
  initDataSourceWebCrawlerForm(appWrapperId, collectionId);
};

window.EasyAI.initDataSourceConfluenceForm = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceConfluenceForm } = await import("../src/easy-ai/dataSourceWeb/confluence/main");
  window.EasyAI.initDataSourceConfluenceForm = initDataSourceConfluenceForm;
  initDataSourceConfluenceForm(appWrapperId, collectionId);
};

window.EasyAI.initDataSourceAttachments = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceAttachments } = await import("../src/easy-ai/dataSourceAttachments/main");
  window.EasyAI.initDataSourceAttachments = initDataSourceAttachments;
  initDataSourceAttachments(appWrapperId, collectionId);
};

window.EasyAI.initProjectPlannerWaitingPanel = async (appWrapperId: string, props: ProjectPlannerWaitingPanelProps) => {
  const { initProjectPlannerWaitingPanel } = await import("../src/easy-ai/projectPlannerWaitingPanel/main");
  window.EasyAI.initProjectPlannerWaitingPanel = initProjectPlannerWaitingPanel;
  initProjectPlannerWaitingPanel(appWrapperId, props);
};

window.EasyAI.initDataSourceBlueSpice = async (appWrapperId: string, collectionId: string) => {
  const { initDataSourceBlueSpice } = await import("../src/easy-ai/dataSourceBlueSpice/main");
  window.EasyAI.initDataSourceBlueSpice = initDataSourceBlueSpice;
  initDataSourceBlueSpice(appWrapperId, collectionId);
};

window.EasyAI.initTeaser = async (appWrapperId: string, props: EasyAITeaserProps) => {
  const { initTeaser } = await import("../src/easy-ai/teaser/main");
  window.EasyAI.initTeaser = initTeaser;
  initTeaser(appWrapperId, props);
};

window.EasyAI.initTeaserProjectPlanner = async (appWrapperId: string) => {
  const { initTeaserProjectPlanner } = await import("../src/easy-ai/teaser/main");
  window.EasyAI.initTeaserProjectPlanner = initTeaserProjectPlanner;
  initTeaserProjectPlanner(appWrapperId);
};

window.EasyAI.initPromptTemplateForm = async (appWrapperId: string, props: PromptTemplateFormProps) => {
  const { initPromptTemplateForm } = await import("../src/easy-ai/promptTemplateForm/main");
  window.EasyAI.initPromptTemplateForm = initPromptTemplateForm;
  initPromptTemplateForm(appWrapperId, props);
};

window.EasyAI.updatePromptTemplateStatus = async (
  promptTemplateId: string,
  status: PromptTemplateStatus,
  htmlElement: HTMLElement
) => {
  const { updatePromptTemplateStatus } = await import(
    "../src/easy-ai/updatePromptTemplateStatus/updatePromptTemplateStatus"
  );
  window.EasyAI.updatePromptTemplateStatus = updatePromptTemplateStatus;
  await updatePromptTemplateStatus(promptTemplateId, status, htmlElement);
};

window.EasyAI.initProjectStatusReportForm = async (appWrapperId: string, props: ProjectStatusReportFormProps) => {
  const { initProjectStatusReportForm } = await import("../src/easy-ai/projectStatusReport/form/main");
  window.EasyAI.initProjectStatusReportForm = initProjectStatusReportForm;
  initProjectStatusReportForm(appWrapperId, props);
};

window.EasyAI.initProjectStatusReportDetail = async (appWrapperId: string, projectStatusReportId: string) => {
  const { initProjectStatusReportDetail } = await import("../src/easy-ai/projectStatusReport/detail/main");
  window.EasyAI.initProjectStatusReportDetail = initProjectStatusReportDetail;
  initProjectStatusReportDetail(appWrapperId, projectStatusReportId);
};

window.EasyAI.initProjectStatusReportSidebar = async (appWrapperId: string, props: ProjectStatusReportSidebarProps) => {
  const { initProjectStatusReportSidebar } = await import("../src/easy-ai/projectStatusReport/sidebar/main");
  window.EasyAI.initProjectStatusReportSidebar = initProjectStatusReportSidebar;
  initProjectStatusReportSidebar(appWrapperId, props);
};
